import React, { useState, useEffect } from 'react';
import { ref, onValue, update, remove } from 'firebase/database';
import { managementDb } from '../firebase'; // Path to Firebase setup
import { toast } from 'react-toastify';
import './ContactManagement.css'; // Path to local CSS file

// Helper function to simulate user confirmation (Replaces global confirm)
// NOTE: This uses window.confirm() for basic functionality but should be replaced
// with a state-controlled modal in production apps.
const handleUserConfirmation = (message) => {
    return window.confirm(message); 
};

const ContactManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState('all'); // all, new, read, responded
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('timestamp'); // timestamp, name, email, status
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    console.log('🔍 Loading contacts from Firebase...');
    const contactsRef = ref(managementDb, 'contacts');
    
    const unsubscribe = onValue(contactsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const contactsArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setContacts(contactsArray);
        
        // Auto-select first contact if none selected or the previous one was deleted
        if (!selectedContact || !contactsArray.some(c => c.id === selectedContact.id)) {
          setSelectedContact(contactsArray[0] || null);
        }
      } else {
        setContacts([]);
        setSelectedContact(null);
      }
    }, (error) => {
      console.error('❌ Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    });

    return () => unsubscribe();
  }, [selectedContact]);

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      const contactRef = ref(managementDb, `contacts/${contactId}`);
      await update(contactRef, { status: newStatus });
      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const markAsRead = async (contactId) => {
    try {
      const contactRef = ref(managementDb, `contacts/${contactId}`);
      await update(contactRef, { status: 'read' });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };
  
  // FIX: Using wrapper function handleUserConfirmation
  const handleDeleteContact = async (contactId) => {
    if (!handleUserConfirmation('Are you sure you want to delete this contact?')) return; 

    try {
        const contactRef = ref(managementDb, `contacts/${contactId}`);
        await remove(contactRef);
        toast.success('Contact deleted successfully');
        if (selectedContact && selectedContact.id === contactId) {
            setSelectedContact(null);
        }
        setSelectedContacts(prev => {
            const newSet = new Set(prev);
            newSet.delete(contactId);
            return newSet;
        });
    } catch (error) {
        console.error('Error deleting contact:', error);
        toast.error('Failed to delete contact');
    }
  };

  // FIX: Using wrapper function handleUserConfirmation
  const handleBulkDelete = async () => {
    if (selectedContacts.size === 0) {
      toast.warning('Please select contacts to delete');
      return;
    }
    
    if (!handleUserConfirmation(`Are you sure you want to delete ${selectedContacts.size} selected contact(s)?`)) return;

    try {
      const deletePromises = Array.from(selectedContacts).map(contactId => {
        const contactRef = ref(managementDb, `contacts/${contactId}`);
        return remove(contactRef);
      });
      
      await Promise.all(deletePromises);
      toast.success(`${selectedContacts.size} contact(s) deleted successfully`);
      
      setSelectedContacts(new Set());
      setSelectAll(false);
      
      if (selectedContact && selectedContacts.has(selectedContact.id)) {
        setSelectedContact(null);
      }
    } catch (error) {
      console.error('Error deleting contacts:', error);
      toast.error('Failed to delete some contacts');
    }
  };

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    // Automatically mark as read if it's new
    if (contact.status === 'new') {
      markAsRead(contact.id);
    }
  };

  const handleContactSelection = (contactId) => {
    setSelectedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const shouldSelect = !selectAll;
    if (shouldSelect) {
      const allContactIds = filteredAndSortedContacts.map(contact => contact.id);
      setSelectedContacts(new Set(allContactIds));
    } else {
      setSelectedContacts(new Set());
    }
    setSelectAll(shouldSelect);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const searchInContact = (contact, searchTerm) => {
    const searchLower = searchTerm.toLowerCase();
    const searchableFields = [
      contact.firstName || '',
      contact.lastName || '',
      contact.email || '',
      contact.phone || '',
      contact.subject || '',
      contact.message || '',
      contact.status || '',
      formatDate(contact.timestamp) || ''
    ];
    
    return searchableFields.some(field => 
      field.toLowerCase().includes(searchLower)
    );
  };

  const filteredAndSortedContacts = contacts
    .filter(contact => {
      const matchesFilter = filter === 'all' || contact.status === filter;
      const matchesSearch = searchTerm === '' || searchInContact(contact, searchTerm);
      return matchesFilter && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        case 'email':
          return (a.email || '').toLowerCase().localeCompare((b.email || '').toLowerCase());
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        case 'timestamp':
        default:
          return b.timestamp - a.timestamp;
      }
    });

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { class: 'badge-new', text: 'NEW', icon: 'fas fa-envelope' },
      read: { class: 'badge-read', text: 'READ', icon: 'fas fa-eye' },
      responded: { class: 'badge-responded', text: 'RESPONDED', icon: 'fas fa-reply' }
    };
    
    const config = statusConfig[status] || statusConfig.new;
    return (
      <span className={`status-badge ${config.class}`}>
        <i className={config.icon}></i> {config.text}
      </span>
    );
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last;
  };

  const getShortDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const currentMessages = filteredAndSortedContacts;
  const totalMessages = filteredAndSortedContacts.length;
  
  const totalNewMessages = contacts.filter(c => c.status === 'new').length;
  const totalReadMessages = contacts.filter(c => c.status === 'read').length;
  const totalRespondedMessages = contacts.filter(c => c.status === 'responded').length;

  // The loading check is removed, content renders immediately based on `contacts` state.

  return (
    <div>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-inbox me-2"></i>
                Contact Management
              </h3>
            </div>
            <div className="card-body">

      <div className="stats-container">
        <div className="stat-card new-stat">
          <div className="stat-icon new">
            <i className="fas fa-envelope"></i>
          </div>
          <div className="stat-content">
            <h3>{totalNewMessages}</h3>
            <p>New</p>
          </div>
        </div>
        <div className="stat-card read-stat">
          <div className="stat-icon read">
            <i className="fas fa-eye"></i>
          </div>
          <div className="stat-content">
            <h3>{totalReadMessages}</h3>
            <p>Read</p>
          </div>
        </div>
        <div className="stat-card responded-stat">
          <div className="stat-icon responded">
            <i className="fas fa-reply-all"></i>
          </div>
          <div className="stat-content">
            <h3>{totalRespondedMessages}</h3>
            <p>Responded</p>
          </div>
        </div>
        <div className="stat-card total-stat">
          <div className="stat-icon total">
            <i className="fas fa-inbox"></i>
          </div>
          <div className="stat-content">
            <h3>{contacts.length}</h3>
            <p>Total</p>
          </div>
        </div>
      </div>

      {/* Enhanced Controls Section - The Gmail Toolbar */}
      <div className="controls-section">
        <div className="controls-left">
          {/* Select All Checkbox and Bulk Actions */}
          <div className="toolbar-actions">
             <button
              className={`select-all-btn ${selectedContacts.size > 0 ? 'selected' : ''}`}
              onClick={handleSelectAll}
              title={selectAll ? 'Deselect All' : 'Select All'}
            >
              <i className={`far ${selectAll ? 'fa-check-square' : 'fa-square'}`}></i>
            </button>
            {selectedContacts.size > 0 && (
              <>
                <button
                  className="toolbar-icon-btn delete-btn"
                  onClick={handleBulkDelete}
                  title={`Delete ${selectedContacts.size} selected`}
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
                <span className="selection-count">{selectedContacts.size} selected</span>
              </>
            )}
            
          </div>

          <div className="filters">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button 
              className={`filter-btn ${filter === 'new' ? 'active' : ''}`}
              onClick={() => setFilter('new')}
            >
              New 
              {totalNewMessages > 0 && <span className="filter-badge">{totalNewMessages}</span>}
            </button>
            <button 
              className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
              onClick={() => setFilter('read')}
            >
              Read
            </button>
            <button 
              className={`filter-btn ${filter === 'responded' ? 'active' : ''}`}
              onClick={() => setFilter('responded')}
            >
              Responded
            </button>
          </div>
        </div>
        
        <div className="controls-right">
          <div className="search-box">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          <div className="sort-controls">
            <label htmlFor="sortSelect" className="sort-label">Sort:</label>
            <select
              id="sortSelect"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="timestamp">Date</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="status">Status</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Gmail-like Layout */}
      <div className="gmail-layout">
        
        {/* Left Sidebar - Contact List */}
        <div className="contact-list-sidebar">
          
          {currentMessages.length === 0 ? (
            <div className="no-contacts">
              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
              <h4>No messages in this view</h4>
              <p className="text-muted">
                {searchTerm ? 'Try adjusting your search terms.' : 'No contact submissions yet.'}
              </p>
            </div>
          ) : (
            currentMessages.map(contact => (
              <div 
                key={contact.id} 
                className={`contact-list-item ${selectedContact?.id === contact.id ? 'active' : ''} ${contact.status === 'new' ? 'new-contact' : ''} ${selectedContacts.has(contact.id) ? 'selected' : ''}`}
                onClick={() => handleContactSelect(contact)}
              >
                <div className="contact-list-checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedContacts.has(contact.id)}
                    onChange={() => handleContactSelection(contact.id)}
                    className="contact-checkbox"
                  />
                </div>
                
                <div className={`contact-list-content-wrapper ${contact.status === 'new' ? 'font-bold' : ''}`}>
                  <div className="contact-list-avatar">
                    <div className="avatar-circle">
                      {getInitials(contact.firstName, contact.lastName)}
                    </div>
                  </div>
                  <div className="contact-list-content">
                    <div className="contact-list-header">
                      <h6 className="contact-list-name">
                        {contact.firstName} {contact.lastName}
                      </h6>
                      <span className="contact-list-date">
                        {getShortDate(contact.timestamp)}
                      </span>
                    </div>
                    <div className="contact-list-subject">
                      {contact.subject}
                    </div>
                    <div className="contact-list-preview">
                      {contact.message ? contact.message.substring(0, 60) + (contact.message.length > 60 ? '...' : '') : 'No message'}
                    </div>
                    <div className="contact-list-meta">
                      {getStatusBadge(contact.status)}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
          
          <div className="contact-list-footer">
            <span className="list-count-chip">
              Showing {totalMessages} message{totalMessages === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        {/* Right Side - Contact Details */}
        <div className="contact-details-panel">
          {selectedContact ? (
            <div className="contact-details">
              <div className="contact-details-subject-bar">
                 <h2 className="contact-details-subject-title">
                     {selectedContact.subject}
                 </h2>
                 <div className="subject-meta-actions">
                    {getStatusBadge(selectedContact.status)}
                     <button 
                        className="toolbar-icon-btn delete-btn"
                        onClick={() => handleDeleteContact(selectedContact.id)}
                        title="Delete this message"
                      >
                       <i className="fas fa-trash-alt"></i>
                    </button>
                 </div>
              </div>

              <div className="contact-details-header">
                <div className="contact-details-avatar">
                  <div className="avatar-circle large">
                    {getInitials(selectedContact.firstName, selectedContact.lastName)}
                  </div>
                </div>
                <div className="contact-details-info">
                  <h4 className="contact-details-name">
                    {selectedContact.firstName} {selectedContact.lastName}
                  </h4>
                  <p className="contact-details-email">
                    {selectedContact.email}
                  </p>
                  <p className="contact-details-date">
                    {formatDate(selectedContact.timestamp)}
                  </p>
                </div>
              </div>
              
              <div className="contact-details-content">
                <div className="contact-details-metadata">
                    <p>
                        <strong>Phone:</strong> {selectedContact.phone}
                    </p>
                </div>
                
                <div className="contact-details-message">
                  <p>{selectedContact.message || "No detailed message provided."}</p>
                </div>
              </div>
              
              <div className="contact-details-actions">
                <div className="status-actions">
                  <i className="fas fa-tag status-tag-icon"></i>
                  <select
                    value={selectedContact.status}
                    onChange={(e) => handleStatusChange(selectedContact.id, e.target.value)}
                    className="status-select"
                  >
                    <option value="new">Mark as New</option>
                    <option value="read">Mark as Read</option>
                    <option value="responded">Mark as Responded</option>
                  </select>
                </div>
                <button
                  className="btn btn-primary reply-btn"
                  onClick={() => window.open(`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`)}
                  title="Reply via Email"
                >
                  <i className="fas fa-reply"></i> Reply
                </button>
              </div>
            </div>
          ) : (
            <div className="no-contact-selected">
              <i className="fas fa-envelope-open fa-3x text-muted mb-3"></i>
              <h4>Select a message</h4>
              <p className="text-muted">
                Choose a message from the list to view details and respond.
              </p>
            </div>
          )}
        </div>
      </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactManagement;